import { useForm } from "react-hook-form";
import {
  Wrapper,
  Header,
  HeaderContent,
  HeaderIcon,
  HeaderTitle,
  HeaderSubtitle,
  CloseButton,
  Body,
  Section,
  SectionTitleWrap,
  SectionMarker,
  SectionTitle,
  SectionNote,
  GridTwoCols,
  InputGroup,
  Label,
  Required,
  InputWrapper,
  IconLeft,
  Input,
  Notice,
  NoticeIcon,
  NoticeTitle,
  NoticeText,
  Footer,
  CancelButton,
  SubmitButton,
  ErrorText,
  RadioGroup,
} from "./regist.modal.style";
import { fetchNewUserRegist } from "../../../api/admin";
import { formatPhoneNumber } from "../../../util/util";

interface ModalProps {
  closedHandler: () => void;
  onRegistSuccess: () => void; // ✅ 새로 추가
}

const RegistModal = ({ closedHandler, onRegistSuccess }: ModalProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      userAccount: "", // 아이디 (영문+숫자만 허용, 직접 입력 필요)
      password: "", // 비밀번호 (정규식 조건에 맞게 입력해야 함)
      confirmPassword: "", // 비밀번호 확인
      userName: "", // 이름
      department: "", // 부서
      jobPosition: "", // 직위
      userEmail: "", // 이메일
      phoneNumber: "", // 전화번호
      role: "user", // ✅ 기본 권한은 사용자
    },
  });

  const companyCode = localStorage.getItem("companyCode");

  const onSubmit = async (data: any) => {
    try {
      if (!companyCode) {
        alert("회사 코드가 없습니다. 다시 로그인해 주세요.");
        return;
      }

      // 비밀번호 확인 검증
      if (data.password !== data.confirmPassword) {
        alert("비밀번호와 비밀번호 확인이 일치하지 않습니다.");
        return;
      }

      console.log(data);

      // API에 맞게 매핑
      const params = {
        companyCode,
        userAccount: data.userAccount,
        password: data.password,
        username: data.userName,
        department: data.department ?? "",
        jobPosition: data.jobPosition ?? "",
        userEmail: data.userEmail ?? "", // ✅ 이메일 필드 추가 필요 시
        phoneNumber: data.phoneNumber ?? "", // ✅ 전화번호 필드 추가 필요 시
      };

      console.log(params);

      const res = await fetchNewUserRegist(params); // ✅ 새로운 API 호출
      console.log("✅ 등록 성공:", res);

      alert("구성원이 성공적으로 등록되었습니다.");
      reset();
      closedHandler();
      onRegistSuccess(); // ✅ 테이블 새로고침
    } catch (error) {
      console.error("❌ 등록 실패:", error);
      alert("구성원 등록에 실패했습니다.");
    }
  };

  const onError = (err: any) => {
    console.log("❌ Validation Error:", err);
    // 배포시 삭제
  };

  const phoneNumberField = register("phoneNumber");

  return (
    <Wrapper onSubmit={handleSubmit(onSubmit, onError)}>
      <Header>
        <HeaderContent>
          <div className="title">
            <HeaderIcon>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </HeaderIcon>
            <div>
              <HeaderTitle>구성원 추가</HeaderTitle>
              <HeaderSubtitle>구성원 정보를 입력해주세요</HeaderSubtitle>
            </div>
          </div>
          <CloseButton type="button" aria-label="닫기" onClick={closedHandler}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </CloseButton>
        </HeaderContent>
      </Header>
      <Body>
        <Section>
          <SectionTitleWrap>
            <SectionMarker />
            <SectionTitle>계정 정보</SectionTitle>
            <SectionNote>(*) 필수 입력 항목</SectionNote>
          </SectionTitleWrap>
          <GridTwoCols>
            <InputGroup>
              <Label htmlFor="regist-companyCode">회사 코드</Label>
              <InputWrapper>
                <IconLeft aria-hidden="true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                    />
                  </svg>
                </IconLeft>
                <Input
                  id="regist-companyCode"
                  type="text"
                  readOnly
                  value={companyCode ?? ""}
                  className="read-only"
                />
              </InputWrapper>
            </InputGroup>
            <InputGroup>
              <Label htmlFor="regist-userAccount">
                아이디 <Required>*</Required>
              </Label>
              <InputWrapper>
                <IconLeft aria-hidden="true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4h16v16H4zM8 8h8M8 12h5M8 16h3"
                    />
                  </svg>
                </IconLeft>
                <Input
                  id="regist-userAccount"
                  type="text"
                  placeholder="아이디를 입력하세요."
                  {...register("userAccount", {
                    required: "아이디는 필수 입력입니다.",
                    pattern: {
                      value: /^[A-Za-z0-9]+$/,
                      message: "아이디는 영문과 숫자만 입력 가능합니다.",
                    },
                  })}
                />
              </InputWrapper>
            </InputGroup>
            <InputGroup>
              <Label htmlFor="regist-password">
                비밀번호 <Required>*</Required>
              </Label>
              <InputWrapper>
                <IconLeft aria-hidden="true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </IconLeft>
                <Input
                  id="regist-password"
                  type="password"
                  placeholder="비밀번호를 입력하세요."
                  {...register("password", {
                    pattern: {
                      value:
                        /^(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+={}:;"'<>,.?/-]).{10,}$/,
                      message:
                        "비밀번호는 최소 10자 이상, 소문자, 숫자, 특수기호가 포함되어야 합니다.",
                    },
                  })}
                />
              </InputWrapper>
            </InputGroup>
            <InputGroup>
              <Label htmlFor="regist-confirmPassword">
                비밀번호 확인 <Required>*</Required>
              </Label>
              <InputWrapper>
                <IconLeft aria-hidden="true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </IconLeft>
                <Input
                  id="regist-confirmPassword"
                  type="password"
                  placeholder="비밀번호를 다시 입력하세요."
                  {...register("confirmPassword", {
                    validate: (value) =>
                      value === watch("password") ||
                      "비밀번호가 일치하지 않습니다",
                  })}
                />
              </InputWrapper>
            </InputGroup>
          </GridTwoCols>
        </Section>
        <Section>
          <SectionTitleWrap>
            <SectionMarker />
            <SectionTitle>구성원 정보</SectionTitle>
          </SectionTitleWrap>
          <GridTwoCols>
            <InputGroup>
              <Label htmlFor="regist-userName">
                이름 <Required>*</Required>
              </Label>
              <InputWrapper>
                <IconLeft aria-hidden="true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </IconLeft>
                <Input
                  id="regist-userName"
                  type="text"
                  placeholder="이름을 입력하세요."
                  {...register("userName", {
                    required: "이름은 필수 입력입니다.",
                  })}
                />
              </InputWrapper>
            </InputGroup>
            <InputGroup>
              <Label htmlFor="regist-department">
                부서명 <Required>*</Required>
              </Label>
              <InputWrapper>
                <IconLeft aria-hidden="true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 3h6v5H9zM3 16h6v5H3zM15 16h6v5h-6zM12 8v4M6 16v-4h12v4"
                    />
                  </svg>
                </IconLeft>
                <Input
                  id="regist-department"
                  type="text"
                  placeholder="부서를 입력하세요."
                  {...register("department", {
                    required: "부서는 필수 입력입니다.",
                  })}
                />
              </InputWrapper>
            </InputGroup>
            <InputGroup>
              <Label htmlFor="regist-jobPosition">직위</Label>
              <InputWrapper>
                <IconLeft aria-hidden="true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2M3 7h18v13H3zM3 11l9 4 9-4M12 13v4"
                    />
                  </svg>
                </IconLeft>
                <Input
                  id="regist-jobPosition"
                  type="text"
                  placeholder="직위 또는 직급"
                  {...register("jobPosition")}
                />
              </InputWrapper>
            </InputGroup>
            <InputGroup>
              <Label htmlFor="regist-userEmail">이메일</Label>
              <InputWrapper>
                <IconLeft aria-hidden="true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </IconLeft>
                <Input
                  id="regist-userEmail"
                  type="email"
                  placeholder="abcd@mail.com"
                  {...register("userEmail")}
                />
              </InputWrapper>
            </InputGroup>
            <InputGroup>
              <Label htmlFor="regist-phoneNumber">전화번호</Label>
              <InputWrapper>
                <IconLeft aria-hidden="true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </IconLeft>
                <Input
                  id="regist-phoneNumber"
                  type="text"
                  maxLength={13}
                  placeholder="010-1234-5678"
                  {...phoneNumberField}
                  onChange={(e) => {
                    e.target.value = formatPhoneNumber(e.target.value);
                    phoneNumberField.onChange(e);
                  }}
                />
              </InputWrapper>
            </InputGroup>
            <InputGroup>
              <Label as="div">
                권한 <Required>*</Required>
              </Label>
              <RadioGroup>
                <label>
                  <input
                    type="radio"
                    value="user"
                    {...register("role", { required: true })}
                  />
                  사용자
                </label>
                <label>
                  <input
                    type="radio"
                    value="admin"
                    {...register("role", { required: true })}
                  />
                  관리자
                </label>
              </RadioGroup>
            </InputGroup>
          </GridTwoCols>
        </Section>
        {errors.userAccount ? (
          <ErrorText>{errors.userAccount.message as string}</ErrorText>
        ) : errors.password ? (
          <ErrorText>{errors.password.message as string}</ErrorText>
        ) : errors.userName ? (
          <ErrorText>{errors.userName.message as string}</ErrorText>
        ) : errors.department ? (
          <ErrorText>{errors.department.message as string}</ErrorText>
        ) : errors.confirmPassword ? (
          <ErrorText>{errors.confirmPassword.message as string}</ErrorText>
        ) : null}
        <Notice>
          <NoticeIcon fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </NoticeIcon>
          <div>
            <NoticeTitle>등록 안내</NoticeTitle>
            <NoticeText>
              아이디는 영문과 숫자로 입력해주세요. 비밀번호는 10자 이상이며
              소문자, 숫자, 특수기호를 포함해야 합니다.
            </NoticeText>
          </div>
        </Notice>
      </Body>
      <Footer>
        <CancelButton type="button" onClick={closedHandler}>
          취소
        </CancelButton>
        <SubmitButton type="submit">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          <span>등록</span>
        </SubmitButton>
      </Footer>
    </Wrapper>
  );
};

export default RegistModal;
